import { Fragment, type ReactElement, useEffect } from 'react';

import type { RouteComponentProps } from '@shared/router';
import * as history from '@shared/router/location';

import * as routes from '@portal/pkg/routes';
import { useSession } from '@portal/pkg/session';
import { PageLoader } from '@portal/views/_shared/page-loader';

/**
 *	The guard every key route sits behind.
 *
 *	It renders a loader instead of its children until the session is known, so
 *	the view below never mounts and never makes a request it has no token for.
 *	`wrapWith` is the only route prop that can do this: `before` runs during
 *	render and cannot read context, and `enter` fires after the view has
 *	already mounted and fetched.
 */
export function RequireSession({
	children,
}: RouteComponentProps): ReactElement {
	const { status, organization } = useSession();

	// In an effect, never in render: history.replace dispatches its change
	// synchronously, which during render would update the router while this
	// component is still rendering. `replace` rather than `push`, so going
	// back does not land on the guard again.
	useEffect(() => {
		if (status === 'anonymous') {
			history.replace(routes.Login());

			return;
		}

		if (status === 'authenticated' && organization === null) {
			history.replace(routes.Organizations());
		}
	}, [status, organization]);

	if (status !== 'authenticated' || organization === null) {
		return <PageLoader />;
	}

	return <Fragment>{children}</Fragment>;
}
